import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';

type AppMessageResponse = {
  data?: {
    id?: number;
    messages?: Record<string, string>;
  };
};

@Injectable({ providedIn: 'root' })
export class AppMessageService {
  private readonly http = inject(HttpClient);
  private readonly storageKey = 'app-messages';
  private readonly messages = signal<Record<string, string>>({});
  private load$?: Observable<Record<string, string>>;

  load(): Observable<Record<string, string>> {
    if (!this.load$) {
      this.loadCachedMessages();
      this.load$ = this.http.get<AppMessageResponse>('api/app-message').pipe(
        map((response) => response?.data?.messages ?? {}),
        tap((messages) => this.set(messages)),
        catchError(() => of(this.messages())),
        shareReplay(1),
      );
    }

    return this.load$;
  }

  set(messages: Record<string, string>): void {
    this.messages.set(messages);
    this.writeCache(messages);
  }

  clear(): void {
    this.messages.set({});
    this.load$ = undefined;
    this.removeCache();
  }

  getMessage(key: string, fallback: string): string {
    return this.messages()[key] ?? fallback;
  }

  private loadCachedMessages(): void {
    const cached = this.readCache();
    if (cached) {
      this.messages.set(cached);
    }
  }

  private readCache(): Record<string, string> | null {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }

    const raw = window.localStorage.getItem(this.storageKey);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as Record<string, string>;
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  private writeCache(messages: Record<string, string>): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(messages));
    } catch {
      return;
    }
  }

  private removeCache(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      window.localStorage.removeItem(this.storageKey);
    } catch {
      return;
    }
  }
}
